import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useAuthUser} from '../../auth/hooks/useAuthUser';
import {startupProfiler} from '../../common/utils/startupProfiler';
import {useOnLogin} from '../../firebase/hooks/useOnLogin';
import {UserDocument} from '../UserDocument';
import {clearCachedUser, getCachedUser, setCachedUser} from '../utils/userCache';
import {useUserDocument} from './useUserDocument';
import {EntityWithMetadata} from '#common/models/Entity';
import {User, UserPrototype} from '#common/users/types';

/**
 * Gets the current user, creating it first if the document does not exist.
 * This hook should only be called once during app initialization.
 * Subsequent uses of the current user should use the provider/context from
 * the useCurrentUser or useOptionalCurrentUser hooks.
 * The user must be logged in prior to calling this hook.
 * @returns the UserDocument for the currently logged in user.
 */
export function useGetOrCreateCurrentUser(): UserDocument {
  const {displayName, email, photoURL, uid: userId} = useAuthUser().data;
  const userDocument = useUserDocument(userId);
  const userStatus = userDocument.status();
  const onLogin = useOnLogin();
  const hasLoggedRef = useRef(false);

  const [cachedUser, setCachedUserState] = useState<EntityWithMetadata<User> | undefined>(() => getCachedUser(userId));

  const prototype: UserPrototype = useMemo(() => {
    const userPrototype: UserPrototype = {email: email!, name: displayName ?? email!};
    if (photoURL) {
      userPrototype.avatar = {source: 'auth', url: photoURL};
    }
    return userPrototype;
  }, [displayName, email, photoURL]);

  const onUserLogin = useCallback(() => {
    void onLogin(prototype);
  }, [onLogin, prototype]);

  useEffect(() => {
    if (userStatus === 'success' && !hasLoggedRef.current) {
      hasLoggedRef.current = true;
      onUserLogin();
    }
  }, [onUserLogin, userStatus]);

  useEffect(() => {
    if (userStatus === 'success') {
      try {
        const liveUser = userDocument.get();
        setCachedUser(userId, liveUser);
        startupProfiler.mark('Current User Document Synced (Firestore)');
      } catch {
        /* v8 ignore next 2 -- @preserve */
        // Document fetch error
      }
    }
  }, [userDocument, userId, userStatus]);

  const optimisticUserDocument: UserDocument = useMemo(() => {
    try {
      if (userStatus === 'error') {
        return userDocument;
      }
      if (userStatus === 'success') {
        userDocument.get();
        return userDocument;
      }
    } catch {
      /* v8 ignore next 2 -- @preserve */
      return userDocument;
    }

    if (cachedUser) {
      return {
        create: (entity) => userDocument.create(entity),
        delete: async () => {
          clearCachedUser(userId);
          setCachedUserState(undefined);
          return userDocument.delete();
        },
        exists: () => true,
        get: () => cachedUser,
        id: userId,
        isLoading: () => false,
        observe: () => {
          const obs = userDocument.observe();
          return {
            ...obs,
            data: cachedUser,
            status: 'success',
          };
        },
        patch: async (update) => {
          const updated = {...cachedUser, ...update};
          setCachedUser(userId, updated);
          setCachedUserState(updated);
          return userDocument.patch(update);
        },
        status: () => 'success',
        update: async (update) => {
          const updated = {...cachedUser, ...update};
          setCachedUser(userId, updated);
          setCachedUserState(updated);
          return userDocument.update(update);
        },
        updateTransactionally: (update) => userDocument.updateTransactionally(update),
      };
    }

    return userDocument;
  }, [cachedUser, userDocument, userId, userStatus]);

  return optimisticUserDocument;
}
